import React, { useCallback, useEffect, useState } from "react";
import { VStack, useToast } from "@chakra-ui/react";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import AudioCard from "../../components/cards/AudioCard";
import FilterBar from "../../components/cards/FilterBar";
import EmptyState from "../../components/placeholders/EmptyState";
import CustomLoader from "../../components/placeholders/CustomLoader";
import { useRecordings } from "../../context/RecordingContext";
import { Recording } from "../../model/recording";
import { RecordingTitleSearchItem } from "../../model/search";
import { extractApiErrorMessage, formatDate } from "../../helpers/utils";
import { getRecordingByID } from "../../services/recording";
import {
  CARD_SPACING,
  DEFAULT_WIDTH,
  PAGE_PADDING,
} from "../../helpers/constant";

const getCategoryLabelKey = (category: string): string => {
  if (category === "zoom_prayer") return "filters.category.zoomPrayer";
  if (category === "prayer_vigil") return "filters.category.nightVigil";
  if (category === "teaching") return "filters.category.teaching";
  if (category === "hymn") return "menu.hymns";
  return "filters.category.exhortation";
};

const getSharePath = (category: string): string => {
  if (category === "zoom_prayer") return "/zoom-prayers";
  if (category === "prayer_vigil") return "/night-vigils";
  if (category === "teaching") return "/teachings";
  return "/exhortations";
};

const SearchPage: React.FC = () => {
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const {
    isLoading,
    addFavorite,
    removeFavorite,
    isFavorite,
    getRecordingFiles,
  } = useRecordings();

  const [searchParams] = useSearchParams();
  const recordingIDQuery = (searchParams.get("q") ?? "").trim();
  const [searchQuery, setSearchQuery] = useState("");
  const [recordingByID, setRecordingByID] = useState<Recording | null>(null);
  const [isLoadingByID, setIsLoadingByID] = useState(false);

  const loadRecordingByID = useCallback(
    async (recordingID: string) => {
      setIsLoadingByID(true);
      try {
        const found = await getRecordingByID(recordingID);
        setRecordingByID(found);
      } catch (error) {
        setRecordingByID(null);
        toast({
          title: t("pages.search.loadErrorTitle"),
          description: extractApiErrorMessage(
            error,
            String(t("common.tryAgain")),
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      } finally {
        setIsLoadingByID(false);
      }
    },
    [t, toast],
  );

  useEffect(() => {
    if (!recordingIDQuery) {
      return;
    }

    loadRecordingByID(recordingIDQuery);
  }, [loadRecordingByID, recordingIDQuery]);

  const handleSuggestionSelect = async (item: RecordingTitleSearchItem) => {
    setSearchQuery("");
    await loadRecordingByID(item.recordingID);
  };

  const isDefaultView = !recordingByID && !isLoadingByID;

  return (
    <VStack w={DEFAULT_WIDTH} px={PAGE_PADDING} mx="auto" align="start">
      <FilterBar
        onSearch={setSearchQuery}
        searchValue={searchQuery}
        placeholder={String(t("pages.search.placeholder"))}
        debounceMs={500}
        showRubricSelect={false}
        onSuggestionSelect={handleSuggestionSelect}
      />
      {isLoading || isLoadingByID ? (
        <CustomLoader />
      ) : isDefaultView ? (
        <EmptyState
          icon={MagnifyingGlassIcon}
          title={t("pages.search.defaultTitle")}
          description={t("pages.search.defaultDescription")}
        />
      ) : !recordingByID ? (
        <EmptyState
          icon={MagnifyingGlassIcon}
          title={t("pages.search.noResultsTitle")}
          description={t("pages.search.noResultsDescription")}
        />
      ) : (
        <VStack spacing={CARD_SPACING} w="full">
          <AudioCard
            key={recordingByID.recordingID}
            recordingID={recordingByID.recordingID}
            title={recordingByID.title}
            preacherName={`${recordingByID.preacherName} • ${t(
              getCategoryLabelKey(recordingByID.category),
            )}`}
            date={formatDate(
              recordingByID.date,
              i18n.language.startsWith("fr") ? "fr" : "en",
            )}
            hasRecording={Boolean(recordingByID.recordingS3Key)}
            hasSummary={Boolean(recordingByID.summaryS3Key)}
            youtubeLink={recordingByID.youtubeLink}
            saved={isFavorite(recordingByID.recordingID)}
            onAddFavorite={() =>
              addFavorite(recordingByID.recordingID, recordingByID)
            }
            onRemoveFavorite={removeFavorite}
            onRequestFiles={(recordingID) => getRecordingFiles(recordingID)}
            sharePath={getSharePath(recordingByID.category)}
          />
        </VStack>
      )}
    </VStack>
  );
};

export default SearchPage;
